import { AGENT_TYPES, RiskAssessmentTypes, GapAssessmentTypes } from "../types/index.ts";
import { ROUTES } from "@/shared/constants/routes.ts";

/**
 * Navigation configuration for "Back to" navigation based on agent type and subtype
 */
type BackNavigation = {
  route: string;
  label: string;
};

/**
 * Determines the correct "Back to" navigation route and label based on agent type and subtype.
 *
 * Handles FAIR assessments in both representations:
 * - AGENT_TYPES.FAIR_ASSESSMENT (standalone agent type)
 * - AGENT_TYPES.RISK_ASSESSMENT + RiskAssessmentTypes.FAIR (subtype pattern)
 *
 * @param agentType - The main agent type
 * @param agentSubType - Optional subtype for the agent (e.g., FAIR, SOC2, etc.)
 * @returns Navigation object with route and label
 */
export const getBackNavigation = (
  agentType: AGENT_TYPES,
  agentSubType?: string | RiskAssessmentTypes | GapAssessmentTypes
): BackNavigation => {
  // Handle FAIR assessments - can be either a separate agent type OR a risk assessment subtype
  // Check FAIR_ASSESSMENT first, then check RISK_ASSESSMENT + FAIR subtype combination
  if (
    agentType === AGENT_TYPES.FAIR_ASSESSMENT ||
    (agentType === AGENT_TYPES.RISK_ASSESSMENT &&
     agentSubType != null &&
     agentSubType === RiskAssessmentTypes.FAIR)
  ) {
    return {
      route: ROUTES.INTERNAL_RISK_ASSESSMENTS,
      label: "Back to Risk Assessments"
    };
  }

  // Handle other agent types
  switch (agentType) {
    case AGENT_TYPES.GAP_ASSESSMENT:
      return {
        route: ROUTES.COMPLIANCE_GAP_ASSESSMENTS,
        label: "Back to Gap Assessments"
      };

    case AGENT_TYPES.RISK_ASSESSMENT:
      // Non-FAIR risk assessments go to gap assessments page
      return {
        route: ROUTES.COMPLIANCE_GAP_ASSESSMENTS,
        label: "Back to Gap Assessments"
      };

    default:
      // Default to third-party vendors page
      return {
        route: ROUTES.THIRD_PARTY_VENDORS_V2,
        label: "Back to Third-Parties"
      };
  }
};
